import {
  DeleteConfirmation,
  ExportDialog,
  SearchInput,
  ExportDropdown,
  Page,
  BackLink,
  IconButton,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import { mimeTypes } from '~/utils';
import EditDrawer from './EditDrawer';
import { ActiveStatusSelect } from '../custom-data/components';
import ViewDrawer from './ViewDrawer';

function PipelineActivityTypesPage() {
  const documentTitle = useDocumentTitle('Pipeline Activity Types');

  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ q: '', isActive: 'true' });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .pipelineActivityTypes()
        .get({ ...params, isActive: params.isActive ?? undefined });
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, params, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [dialog, setDialog] = useState(null);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onClose={resolve}
        onLoad={api.www
          .workspaces(workspace.id)
          .pipelineActivityTypes()
          .get(
            { ...params, isActive: params.isActive ?? undefined },
            { headers: { accept: mimeType }, responseType: 'blob' },
          )}
      />
    ));
  };

  const handleEdit = async (pipelineActivityType) => {
    setDialog(
      <EditDrawer
        pipelineActivityType={pipelineActivityType}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Pipeline Activity Types');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  const handleView = async (pipelineActivityType) => {
    setDialog(
      <ViewDrawer
        pipelineActivityType={pipelineActivityType}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Pipeline Activity Types');
        }}
      />,
    );
  };

  async function handleActiveStatusChange(item, flag) {
    try {
      await api.www.workspaces(workspace.id).pipelineActivityTypes(item.id).setActiveStatus(flag);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  const handleDelete = async (pipelineActivityType) => {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>
        Are you sure you want to delete this Pipeline Activity Type?
      </DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .pipelineActivityTypes(pipelineActivityType ? pipelineActivityType.id : undefined)
        .delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  };

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Pipeline Activity Types</Page.Title>
          <Page.Description>
            Pipeline activity types are used to track the types of activities that occur during the sales process.
          </Page.Description>
        </Page.Info>

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('pipeline_activity_types.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('pipeline_activity_types.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="plus" tooltip="New Pipeline Activity Type" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleFilterChange} />

          <ActiveStatusSelect value={params.isActive} onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column width="20rem">Name</ListView.Column>
            <ListView.Column>Description</ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>
          <ListView.Body>
            {data.map((item) => {
              const { id, name, description, isActive, isRestricted } = item;

              return (
                <ListView.Row
                  onClick={() => {
                    isRestricted ? handleView(item) : handleEdit(item);
                  }}
                  key={id}
                  isDisabled={!isActive}>
                  <ListView.Cell>{name}</ListView.Cell>
                  <ListView.Cell>{description}</ListView.Cell>

                  <ListViewActions>
                    {isRestricted ? (
                      <ListViewActions.View onClick={() => handleView(item)} />
                    ) : (
                      <>
                        <ListViewActions.Edit onClick={() => handleEdit(item)} />

                        <hr />

                        <ListViewMenu>
                          {({ setIsOpen }) => (
                            <>
                              <ListViewMenu.Item onClick={() => handleEdit(item)}>Edit</ListViewMenu.Item>
                              <ListViewMenu.Item
                                onClick={async () => {
                                  await handleActiveStatusChange(item, !isActive);
                                  setIsOpen(false);
                                }}>
                                {isActive ? 'Deactivate' : 'Activate'}
                              </ListViewMenu.Item>
                              <ListViewMenu.DeleteItem
                                tooltip="This item is currently in use."
                                onCheckDependencies={async (workspace) =>
                                  (await workspace.pipelineActivityTypes(id).hasDependencies()).data
                                }
                                onClick={() => handleDelete(item)}>
                                Delete
                              </ListViewMenu.DeleteItem>
                            </>
                          )}
                        </ListViewMenu>
                      </>
                    )}
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>
          <ListView.Status total={data.length} label="Pipeline Activity Type" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}

export default PipelineActivityTypesPage;
